import { useEffectOnce } from 'react-use';
import {
  Card,
  Checkbox,
  FieldSet,
  Flex,
  FormHelp,
  Heading,
  IconCalendarTime,
  IconTrending,
  IconTrophy,
  Label,
  LabeledControl,
  Legend,
  List,
  Paragraph,
  Radio,
  Text,
} from '@zillow/constellation';
import { PaymentsRequestIcon } from './2PaymentsRequestIcon';
import { FIELD_IDS } from '../../../../constants/wizard';
import { Controller, useFormContext } from 'react-hook-form';
import { isValidNonNull } from '../../../../utils/validation';
import {
  emitPaymentRequestCheckEvent,
  emitPaymentRequestNoEvent,
  emitPaymentRequestYesEvent,
  emitViewPaymentRequestStepEvent,
} from '../../../../analytics/clickstreamEvents';
import { usePaymentRequestTreatment } from '../../../../utils/ab-tests';
import { useEffect } from 'react';
import type { Answers } from '../../../../graphql/queries';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';

export function PaymentRequestStep() {
  const {
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();
  const applicationContext = useApplicationContextOrUndefined();
  const test = usePaymentRequestTreatment();

  useEffectOnce(() => {
    emitViewPaymentRequestStepEvent(applicationContext);
  });

  // Initialize requestZillowPayments field when component mounts
  useEffect(() => {
    if (test !== 'on_1a') {
      // Only initialize to false on the "checkbox" variant,
      // since it's the one that is not required, so we run
      // the risk of the user not clicking the checkbox so the endpoint
      // will not be called.

      // We don't need to manually alter the value for the "radio" variant
      // since we don't want to show any option selected initially
      return;
    }
    const currentValue = getValues(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS);
    // Only initialize to false if the field is currently null/undefined
    // This ensures we don't override an existing user choice
    if (currentValue == null) {
      setValue(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS as keyof Answers, false, {
        shouldDirty: false, // Don't mark as dirty since this is initialization
        shouldTouch: false, // Don't mark as touched since user hasn't interacted
      });
    }
  }, [getValues, setValue]);

  return (
    <Flex display="flex" flexDirection="column" gap="sm" alignItems="flex-start">
      <PaymentsRequestIcon height={75} />
      <Heading level={4} as="h2" fontFamily="serif">
        Do you want to build credit when you pay rent on time?
      </Heading>
      <List appearance="none">
        <List.Item>
          <Paragraph>
            <IconCalendarTime marginRight="xs" />
            <Text>Zillow reports on time payments to Experian, a major credit bureau.</Text>
          </Paragraph>
        </List.Item>
        <List.Item>
          <Paragraph>
            <IconTrending marginRight="xs" />
            <Text>You could build your credit in just a few months.</Text>
          </Paragraph>
        </List.Item>
        <List.Item>
          <Paragraph>
            <IconTrophy marginRight="xs" />
            <Text>Building credit can help you qualify for future loans, buy a home, and more.</Text>
          </Paragraph>
        </List.Item>
      </List>
      <Paragraph>Showing your interest now will not affect your application.</Paragraph>
      {test === 'on_1a' && (
        <Controller
          name={FIELD_IDS.REQUEST_ZILLOW_PAYMENTS}
          render={({ field: { onBlur, value, onChange } }) => (
            <LabeledControl
              onBlur={onBlur}
              label={<Label>Ask landlord to use Zillow payments if application is accepted</Label>}
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => {
                    onChange(e.target.checked);
                    if (e.target.checked) {
                      emitPaymentRequestCheckEvent(applicationContext);
                    }
                  }}
                />
              }
            />
          )}
        />
      )}

      {test === 'on_1b' && (
        <Controller
          name={FIELD_IDS.REQUEST_ZILLOW_PAYMENTS}
          rules={{ validate: isValidNonNull }}
          render={({ field: { onBlur, value, onChange } }) => (
            <FieldSet
              onBlur={onBlur}
              legend={<Legend>Request Zillow payments</Legend>}
              description={
                errors[FIELD_IDS.REQUEST_ZILLOW_PAYMENTS] && (
                  <FormHelp>{errors[FIELD_IDS.REQUEST_ZILLOW_PAYMENTS].message}</FormHelp>
                )
              }
            >
              <LabeledControl
                onChange={() => {
                  onChange(true);
                  emitPaymentRequestYesEvent(applicationContext);
                }}
                label={<Label>Yes, ask landlord to use Zillow payments if application is accepted</Label>}
                control={<Radio name="zillow-payments-opt-in" defaultChecked={value} />}
              />

              <LabeledControl
                onChange={() => {
                  onChange(false);
                  emitPaymentRequestNoEvent(applicationContext);
                }}
                label={<Label>No, not interested at this time</Label>}
                control={<Radio name="zillow-payments-opt-in" defaultChecked={value === false} />}
              />
            </FieldSet>
          )}
        />
      )}
    </Flex>
  );
}

export function PaymentRequestTip() {
  return (
    <Card cardElevation={0} cardType="secondary">
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        The benefits of Zillow payments
      </Heading>
      <List>
        <List.Item>
          <strong>More ways to pay </strong>
          with your debit/credit card or bank account.
        </List.Item>
        <List.Item>
          <strong>Pay on the go, </strong>
          or schedule your payments on time.
        </List.Item>
        <List.Item>
          <strong>Security is our top priority, </strong>
          and we never sell your information.
        </List.Item>
      </List>
    </Card>
  );
}
